import { useCallback, useEffect, useState } from "react";
import { AMBIANCES, makeTokens, type EffectiveMode, type Mode } from "./ambiances.js";

/**
 * Apparence par appareil (cahier des charges §12.2) : thème clair/sombre/système, ambiance, tailles
 * de boutons et de texte, image de fond. Conservée en `localStorage` (locale à l'appareil, jamais en
 * base), appliquée comme variables CSS sur le conteneur racine.
 */

const LS = {
  get<T>(key: string, fallback: T): T {
    try {
      const v = localStorage.getItem(key);
      return v == null ? fallback : (JSON.parse(v) as T);
    } catch {
      return fallback;
    }
  },
  set(key: string, value: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* quota / mode privé : on ignore */
    }
  },
};

export type InterfaceMode = "simple" | "advanced";
export type RecordingQuality = "high" | "medium" | "low";

export interface Appearance {
  mode: Mode;
  setMode: (m: Mode) => void;
  ambiance: string;
  setAmbiance: (k: string) => void;
  effMode: EffectiveMode;
  btnSize: number;
  setBtnSize: (n: number) => void;
  textSize: number;
  setTextSize: (n: number) => void;
  bgImage: string;
  setBgImage: (css: string) => void;
  interfaceMode: InterfaceMode;
  setInterfaceMode: (m: InterfaceMode) => void;
  /** Force la disposition étroite (mobile) même sur grand écran. Local à l'appareil. */
  forceNarrow: boolean;
  setForceNarrow: (v: boolean) => void;
  /**
   * Largeur maximale (px) des fenêtres de détail sur grand écran. Sans effet sur mobile, où la
   * fenêtre est déjà plus étroite que le plafond. Local à l'appareil.
   */
  winMaxWidth: number;
  setWinMaxWidth: (px: number) => void;
  quality: RecordingQuality;
  setQuality: (q: RecordingQuality) => void;
  bgDaily: string;
  setBgDaily: (css: string) => void;
  bgStandalone: string;
  setBgStandalone: (css: string) => void;
  /** Identifiant du micro choisi (vide = périphérique par défaut du système). Local à l'appareil. */
  micDeviceId: string;
  setMicDeviceId: (id: string) => void;
}

export function useAppearance(): Appearance {
  const [ambiance, setAmbianceState] = useState<string>(() => LS.get("capt_amb", "bleu"));
  const [mode, setModeState] = useState<Mode>(() =>
    LS.get<Mode | null>("capt_mode", null) ?? (AMBIANCES[LS.get("capt_amb", "bleu")]?.prefersDark ? "dark" : "light"),
  );
  const [btnSize, setBtnSizeState] = useState<number>(() => LS.get("capt_btnsize", 1));
  const [textSize, setTextSizeState] = useState<number>(() => LS.get("capt_textsize", 1));
  const [bgImage, setBgImageState] = useState<string>(() => LS.get("capt_bg", ""));
  // Interface SIMPLE par défaut (§7.4) : un appareil qui n'a jamais choisi démarre au plus dépouillé
  // — seule la note cumulée, contrôles secondaires masqués. Un choix déjà fait est conservé tel quel.
  const [interfaceMode, setInterfaceModeState] = useState<InterfaceMode>(() => LS.get("capt_ui", "simple"));
  const [forceNarrow, setForceNarrowState] = useState<boolean>(() => LS.get("capt_narrow", false));
  const [winMaxWidth, setWinMaxWidthState] = useState<number>(() => LS.get("capt_winmax", 1100));
  const [quality, setQualityState] = useState<RecordingQuality>(() => LS.get("capt_quality", "high"));
  const [bgDaily, setBgDailyState] = useState<string>(() => LS.get("capt_bgday", ""));
  const [bgStandalone, setBgStandaloneState] = useState<string>(() => LS.get("capt_bgstd", ""));
  const [micDeviceId, setMicDeviceIdState] = useState<string>(() => LS.get("capt_mic", ""));

  const [sysDark, setSysDark] = useState<boolean>(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-color-scheme: dark)").matches,
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const h = (e: MediaQueryListEvent) => setSysDark(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);

  const effMode: EffectiveMode = mode === "system" ? (sysDark ? "dark" : "light") : mode;

  // Persistance
  useEffect(() => LS.set("capt_amb", ambiance), [ambiance]);
  useEffect(() => LS.set("capt_mode", mode), [mode]);
  useEffect(() => LS.set("capt_btnsize", btnSize), [btnSize]);
  useEffect(() => LS.set("capt_textsize", textSize), [textSize]);
  useEffect(() => LS.set("capt_bg", bgImage), [bgImage]);
  useEffect(() => LS.set("capt_ui", interfaceMode), [interfaceMode]);
  useEffect(() => LS.set("capt_narrow", forceNarrow), [forceNarrow]);
  useEffect(() => LS.set("capt_winmax", winMaxWidth), [winMaxWidth]);
  useEffect(() => LS.set("capt_quality", quality), [quality]);
  useEffect(() => LS.set("capt_bgday", bgDaily), [bgDaily]);
  useEffect(() => LS.set("capt_bgstd", bgStandalone), [bgStandalone]);
  useEffect(() => LS.set("capt_mic", micDeviceId), [micDeviceId]);

  // Application des jetons de thème au conteneur racine + fond de page
  useEffect(() => {
    const tokens = makeTokens(ambiance, effMode);
    const el = document.getElementById("approot");
    if (el) {
      for (const [k, v] of Object.entries(tokens)) el.style.setProperty("--" + k, v);
    }
    document.body.style.background = effMode === "dark" ? "#0b0b0d" : "#e9e9ee";
  }, [ambiance, effMode]);

  const setAmbiance = useCallback((k: string) => {
    setAmbianceState(k);
    if (AMBIANCES[k]?.prefersDark) setModeState("dark");
  }, []);

  return {
    mode,
    setMode: setModeState,
    ambiance,
    setAmbiance,
    effMode,
    btnSize,
    setBtnSize: setBtnSizeState,
    textSize,
    setTextSize: setTextSizeState,
    bgImage,
    setBgImage: setBgImageState,
    interfaceMode,
    setInterfaceMode: setInterfaceModeState,
    winMaxWidth,
    setWinMaxWidth: setWinMaxWidthState,
    forceNarrow,
    setForceNarrow: setForceNarrowState,
    quality,
    setQuality: setQualityState,
    bgDaily,
    setBgDaily: setBgDailyState,
    bgStandalone,
    setBgStandalone: setBgStandaloneState,
    micDeviceId,
    setMicDeviceId: setMicDeviceIdState,
  };
}
